import { endOfMonth, startOfDay, startOfMonth, subDays, subMonths } from 'date-fns';

import type { OrderPlatform } from '../../orders/order.model';
import type { SizeKey } from '../../orders/size.utils';

export const MAX_HISTORY_DAYS = 365;

export type DatePresetKey =
  | 'last7Days'
  | 'last30Days'
  | 'last90Days'
  | 'thisMonth'
  | 'lastMonth'
  | 'last6Months'
  | 'last12Months';

export type PlatformFilter = OrderPlatform | 'all';

export interface StatisticsFilters {
  preset: DatePresetKey | 'custom';
  from: Date;
  to: Date;
  platform: PlatformFilter;
  locationId: string | 'all';
  sizeKey: SizeKey | 'all';
}

interface DatePreset {
  key: DatePresetKey;
  label: string;
  range: (today: Date) => { from: Date; to: Date };
}

const lastDays = (days: number) => (today: Date) => ({ from: subDays(today, days - 1), to: today });

export const DATE_PRESETS = [
  { key: 'last7Days', label: 'Last 7 days', range: lastDays(7) },
  { key: 'last30Days', label: 'Last 30 days', range: lastDays(30) },
  { key: 'last90Days', label: 'Last 90 days', range: lastDays(90) },
  { key: 'thisMonth', label: 'This month', range: today => ({ from: startOfMonth(today), to: today }) },
  {
    key: 'lastMonth',
    label: 'Last month',
    range: today => ({ from: startOfMonth(subMonths(today, 1)), to: startOfDay(endOfMonth(subMonths(today, 1))) })
  },
  { key: 'last6Months', label: 'Last 6 months', range: lastDays(183) },
  { key: 'last12Months', label: 'Last 12 months', range: lastDays(MAX_HISTORY_DAYS) },
] as const satisfies readonly DatePreset[];

export const PLATFORM_OPTIONS = [
  { value: 'all', label: 'All', icon: null },
  { value: 'ios', label: 'iOS', icon: '/apple.svg' },
  { value: 'android', label: 'Android', icon: '/android.svg' },
] as const satisfies readonly { value: PlatformFilter; label: string; icon: string | null }[];

export const minSelectableDate = (today = new Date()): Date =>
  startOfDay(subDays(today, MAX_HISTORY_DAYS - 1));

export const presetRange = (
  key: DatePresetKey,
  today = startOfDay(new Date())
): Pick<StatisticsFilters, 'preset' | 'from' | 'to'> => {
  const preset = DATE_PRESETS.find(p => p.key === key)!;
  return { preset: key, ...preset.range(today) };
};

export const defaultStatisticsFilters = (): StatisticsFilters => ({
  ...presetRange('last30Days'),
  platform: 'all',
  locationId: 'all',
  sizeKey: 'all',
});

export const presetLabel = (key: StatisticsFilters['preset']): string =>
  DATE_PRESETS.find(p => p.key === key)?.label ?? 'Custom range';
